import { Cpu, Plus, RefreshCw, Shield, Zap } from 'lucide-react';
import { useAgents } from '../lib/hooks';
import { useAuth } from '../lib/auth-context';
import { useState } from 'react';
import { FleetStatusBanner } from '../components/FleetStatusBanner';

function formatRelativeTime(date: Date): string {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function Agents() {
  const { isConnected, api } = useAuth();
  const { data, isLoading, refetch } = useAgents();
  const [creating, setCreating] = useState(false);

  const agents = data?.agents ?? [];
  const tenantStats = data?.tenantStats;

  const handleCreateAgent = async (name: string, runtime: string) => {
    if (!api) return;
    setCreating(true);
    try {
      await api.createAgent(name, runtime);
      refetch();
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="p-8">
      {isConnected && <FleetStatusBanner />}

      {/* Tenant-wide stats banner */}
      {isConnected && tenantStats && (
        <div className="bg-sg-card border border-sg-border rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-400 mb-1">Tenant Activity (All Agents)</div>
              <div className="flex items-center gap-6">
                <div>
                  <span className="text-2xl font-bold text-white">{tenantStats.eventsLast24h || 0}</span>
                  <span className="text-sm text-gray-400 ml-2">sessions (24h)</span>
                </div>
                {tenantStats.lastActivity && (
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${tenantStats.isActive ? 'bg-green-500' : 'bg-gray-500'}`} />
                    <span className="text-sm text-gray-400">
                      Last: {formatRelativeTime(new Date(tenantStats.lastActivity))}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-white">Agent Configurations</h2>
          <p className="text-gray-400 mt-1">Manage deployment configs and credentials</p>
        </div>
        {isConnected && (
          <div className="flex items-center gap-2">
            <button onClick={refetch} className="p-2 text-gray-400 hover:text-white transition-colors" title="Refresh">
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => handleCreateAgent('New Agent', 'node-express')}
              disabled={creating}
              className="flex items-center gap-2 px-4 py-2 bg-sg-accent text-white rounded-lg text-sm font-medium hover:bg-sg-accent/90 transition-colors disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              {creating ? 'Deploying...' : 'Deploy Agent'}
            </button>
          </div>
        )}
      </div>

      {!isConnected ? (
        <div className="bg-sg-card border border-sg-border rounded-xl p-12 text-center">
          <Cpu className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500">Connect to the control plane in Settings</p>
        </div>
      ) : agents.length === 0 ? (
        <div className="bg-sg-card border border-sg-border rounded-xl p-12 text-center">
          <Cpu className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500">{isLoading ? 'Loading agents...' : 'No agents deployed yet'}</p>
          <p className="text-gray-600 text-sm mt-2">Deploy your first agent to start collecting telemetry</p>
          <div className="mt-6 grid grid-cols-3 gap-4 max-w-lg mx-auto text-left">
            <div className="bg-sg-dark border border-sg-border rounded-lg p-4">
              <div className="text-sm font-medium text-white mb-1">Python</div>
              <div className="text-xs text-gray-500">pip install shadowguard</div>
            </div>
            <div className="bg-sg-dark border border-sg-border rounded-lg p-4">
              <div className="text-sm font-medium text-white mb-1">Node.js</div>
              <div className="text-xs text-gray-500">npm i @shadowguard/agent</div>
            </div>
            <div className="bg-sg-dark border border-sg-border rounded-lg p-4">
              <div className="text-sm font-medium text-white mb-1">Cloudflare</div>
              <div className="text-xs text-gray-500">Worker middleware</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {agents.map((agent) => {
            const mon = agent.monitorState || 'never_seen';
            const monColor =
              mon === 'healthy'
                ? 'text-emerald-400'
                : mon === 'quiet' || mon === 'stale'
                  ? 'text-sky-300'
                  : mon === 'degraded'
                    ? 'text-orange-400'
                    : mon === 'dark'
                      ? 'text-red-400'
                      : 'text-gray-500';
            return (
              <div key={agent.id} className="bg-sg-card border border-sg-border rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Cpu className="w-5 h-5 text-blue-400" />
                    <h3 className="font-semibold text-white">{agent.name}</h3>
                  </div>
                  <span className={`text-xs font-bold uppercase tracking-wide ${monColor}`}>
                    {mon}
                  </span>
                </div>

                {/* Config Info */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">Runtime</span>
                    <span className="text-sm text-white font-mono">{agent.runtime}</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">Agent ID</span>
                    <span className="text-xs text-gray-500 font-mono">{agent.id.substring(0, 8)}...</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">Last success</span>
                    <span className="text-sm text-white">
                      {agent.lastIngestOkAt || agent.lastHeartbeat
                        ? formatRelativeTime(new Date((agent.lastIngestOkAt || agent.lastHeartbeat)!))
                        : 'never'}
                    </span>
                  </div>

                  {agent.monitorCause && (
                    <div className="text-xs text-orange-300 font-mono">{agent.monitorCause}</div>
                  )}
                  {agent.monitorDetail && (
                    <div className="text-xs text-gray-500">{agent.monitorDetail}</div>
                  )}

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">Created</span>
                    <span className="text-sm text-white">{agent.deployedAt ? new Date(agent.deployedAt).toLocaleDateString() : 'N/A'}</span>
                  </div>

                  <div className="pt-3 border-t border-sg-border">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-4 h-4 text-green-400" />
                      <span className="text-xs text-gray-400">
                        {agent.encryptionKeyConfigured === 0
                          ? 'ENCRYPTION KEY MISSING'
                          : 'TLS Encryption Active'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-blue-400" />
                      <span className="text-xs text-gray-400">API Key Authentication</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
